import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import moment from 'moment';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import PhoneIcon from '@mui/icons-material/Phone';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Dialog from '@mui/material/Dialog';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { Link } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import useNotification from '../../../../../components/_hook/useNotification';
import useKeepitalRoutes from '../../../../../components/_hook/useKeepitalRoutes';
import useCurrency from '../../../../../components/_hook/useCurrency';
import useLanguage from '../../../../../components/_hook/useLanguage';
import useRoutes from '../../../../../components/_hook/useRoutes';
import useRouter from '../../../../../components/_hook/useRouter';
import ProcurementLayout from '../../../../../layout/Procurement';
import SchemaCompanyName from '../../../../../components/_schema/SchemaCompanyName';
import SchemaUserName from '../../../../../components/_schema/SchemaUserName';
import { useOrderAskForQuoteQuery } from '../../../../../../graphql/query/orderAskForQuote/useOrderAskForQuoteQuery';
import BaseImage from '../../../../../components/_image/BaseImage';
import NotFound from '../../../../../components/_error/NotFound';
import Helmet from '../../../../../components/Helmet';
import ProcurementIncomingAskForQuoteDetailRelatedQuotationTable from './_module/ProcurementIncomingAskForQuoteDetailRelatedQuotationTable';
import { useMarkReadOrderAskForQuotesMutation } from '../../../../../../graphql/mutation/orderAskForQuote/markReadOrderAskForQuotesMutation';
import Context from '../../../../../context';
import SchemaUserAvatar from '../../../../../components/_schema/SchemaUserAvatar';
import SchemaUserEmail from '../../../../../components/_schema/SchemaUserEmail';
import tel from '../../../../../components/util/tel';
import ButtonFollow from '../../../../../components/_button/ButtonFollow';
import UserFollow from '../../../../../components/constant/userFollow';
import user from '../../../../../components/util/user';
import { useChatroomLazyQuery } from '../../../../../../graphql/query/chatroom/useChatroomQuery';
import Chatroom from '../../../../../adminComponents/chatroom/Chatroom';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import { useProposeQuotationOrderAskForQuoteMutation } from '../../../../../../graphql/mutation/orderAskForQuote/proposeQuotationOrderAskForQuoteMutation';
import plan from '../../../../../components/util/plan';
import FreePackagePlanSubscriptionPaper from '../../../../../components/_paper/FreePackagePlanSubscriptionPaper';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
  paperQuotation: {
    margin: theme.spacing(1),
    width: '100%',
  },
  message: {
    whiteSpace: 'pre-wrap',
  },
  closedDateDanger: {
    color: '#F92949',
  },
  productQuantity: {
    color: '#F92949',
  },
  bold: {
    fontWeight: 'bold',
  },
  blur: {
    filter: 'blur(5px)',
    pointerEvents: 'none',
    userSelect: 'none',
  },
}));

function ProcurementIncomingOrderAskForQuoteDetail() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { path: keepitalPath } = useKeepitalRoutes();
  const { docID: orderAskForQuoteId } = params;
  const { getText } = useLanguage();
  const { getSymbol } = useCurrency();
  const { company, user: contextUser } = useContext(Context);
  const [chatroomID, setChatroomID] = useState<string | null>(null);
  const [isOpenMessageDialog, setIsOpenMessageDialog] = useState(false);
  const isFreePackage = plan.isFreePackage(company.planData);

  const { data, loading } = useOrderAskForQuoteQuery(
    `
			_id 
            buyer {
              user 
              userData {
                 _id 
                 emails {
                    id
                    primary
                    status
                    address
                  }
                 profile {
                    name {
                      firstname
                      lastname
                    }
                    works {
                      id
                      startedAt
                      stoppedAt
                      position {
                        la
                        text
                      }
                      contact {
                        emails {
                          id
                          primary
                          status
                          address
                          verified
                        }
                        tel {
                          id
                          title {
                            la
                            text
                          }
                          status
                          countryCode
                          number
                        }
                      }
                    }
                    contact {
                        tel {
                          id
                          title {
                            la
                            text
                          }
                          status
                          countryCode
                          number
                        }
                    }
                 }
                 currentUserFollowData {
                  _id 
                 }
              }
              company 
              companyData {
                _id
                verification
                name {
                    la
                    text
                }
                origin
                slug
              }
            }
            seller {
              company 
              companyData {
                _id
                name {
                    la
                    text
                }
                origin
                slug
                departments {
                  id
                  name {
                      la
                      text
                  }
                }
                custom_fields {
                  id
                  locked
                  title {
                    la
                    text
                  }
                  description {
                    la
                    text
                  }
                  field_type
                  data_type
                  attributes_input
                  attributes
                  currency
                }
              }
            }
            message 
            department
            closed
            relatedOrders {
              _id
              details {
                status
              }
              seller {
                company {
                  id
                  companyData {
                    _id
                    name {
                        la
                        text
                    }
                    origin
                    slug
                  }
                }
              }
              prices {
                sub_total
              }
              payment {
                currency
              }
              updated {
                time 
              }
              created {
                  time 
              }
            }
            items {
              id
              quantity
              variations
              productData {
                _id
                company
                companyData {
				    _id
					origin
					slug
					name {
                        la
                        text
                    }
				}
				name {
					la
					text
				}
				slug
				variations {
				  id
				  attributes
				}
				custom_fields {
				  id
				  status
				}
				prices {
				  currency
				  final
				}
				media {
					images {
						status 
						filename
						filename_small
		
						filenameUrl
						filenameSmallUrl
					}
				}
				infos {
				    unit {
                        la
                        text
                    }
				}
              }
            }
            updated {
                time 
            }
            created {
                time 
            }
		`,
    {
      variables: {
        filter: {
          _id: orderAskForQuoteId as string,
          sellerCompanyId: company._id,
        },
      },
    }
  );

  const [
    markReadOrderAskForQuotesMutation,
  ] = useMarkReadOrderAskForQuotesMutation(
    `
    updatedOrderAskForQuotes {
      _id
      seen
    }
  `,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const [getChatrooms] = useChatroomLazyQuery(
    `
        _id
      `,
    {
      onCompleted: (tempData) => {
        if (
          tempData &&
          tempData.chatrooms &&
          tempData.chatrooms.items &&
          tempData.chatrooms.items.length
        ) {
          setChatroomID(tempData.chatrooms.items[0]._id);
        }
      },
    }
  );

  const [
    proposeQuotationOrderAskForQuoteMutation,
    { loading: isProposingQuotationOrderAskForQuote },
  ] = useProposeQuotationOrderAskForQuoteMutation(
    `
    createdQuotation {
      _id
    }
  `,
    {
      onCompleted: (tempData) => {
        if (
          tempData &&
          tempData.proposeQuotationOrderAskForQuote &&
          tempData.proposeQuotationOrderAskForQuote.createdQuotation &&
          tempData.proposeQuotationOrderAskForQuote.createdQuotation._id
        ) {
          notification.success(t('done'));
          history.push(
            path('procurement.outgoing.quotation', {
              companyID: company._id,
              docID:
                tempData.proposeQuotationOrderAskForQuote.createdQuotation._id,
            })
          );
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const orderAskForQuote =
    orderAskForQuoteId && data ? data.orderAskForQuotes.items[0] : null;

  function onClickProposeQuotation() {
    if (orderAskForQuote) {
      proposeQuotationOrderAskForQuoteMutation({
        variables: {
          _id: orderAskForQuote._id,
          companyId: company._id,
        },
      });
    }
  }

  useEffect(() => {
    if (orderAskForQuote) {
      getChatrooms({
        variables: {
          filter: {
            users: [
              {
                id: contextUser._id,
                from: company._id,
              },
              {
                id: orderAskForQuote.buyer
                  ? orderAskForQuote.buyer.user
                  : undefined,
                from: orderAskForQuote.buyer
                  ? orderAskForQuote.buyer.company
                  : undefined,
              },
            ],
            companyId: company._id,
            userId: contextUser._id,
          },
        },
      });
    }
  }, [JSON.stringify(orderAskForQuote)]);

  useEffect(() => {
    if (orderAskForQuote) {
      markReadOrderAskForQuotesMutation({
        variables: {
          _ids: [orderAskForQuote._id],
          sellerCompanyId: company._id,
        },
      });
    }
  }, [JSON.stringify(orderAskForQuote)]);

  if (loading && orderAskForQuoteId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  if (!orderAskForQuote) {
    return <NotFound />;
  }

  const companyDepartments =
    orderAskForQuote.seller &&
    orderAskForQuote.seller.companyData &&
    orderAskForQuote.seller.companyData.departments
      ? orderAskForQuote.seller.companyData.departments
      : [];
  const department = orderAskForQuote.department
    ? companyDepartments.find(
        (companyDepartment) =>
          companyDepartment.id === orderAskForQuote.department
      )
    : null;

  const buyerTel =
    orderAskForQuote.buyer && orderAskForQuote.buyer.userData
      ? user.getTel(
          orderAskForQuote.buyer.userData,
          orderAskForQuote.buyer.company
        )
      : null;

  const buyerWorkProfile =
    orderAskForQuote.buyer && orderAskForQuote.buyer.userData
      ? user.getWorkProfile(
          orderAskForQuote.buyer.userData,
          orderAskForQuote.buyer.company
        )
      : null;

  const companyCustomFields =
    orderAskForQuote.seller &&
    orderAskForQuote.seller.companyData &&
    orderAskForQuote.seller.companyData.custom_fields
      ? orderAskForQuote.seller.companyData.custom_fields
      : [];

  return (
    <>
      <Helmet title={`${t('enquiry')} | ${t('procurement')}`} />
      <Dialog
        open={isOpenMessageDialog}
        onClose={() => setIsOpenMessageDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <Chatroom
          isModal
          onClose={() => setIsOpenMessageDialog(false)}
          chatroomId={chatroomID}
          onChatroomCreated={(chatroomId) => setChatroomID(chatroomId)}
          receiver={{
            userId: orderAskForQuote.buyer
              ? orderAskForQuote.buyer.user
              : undefined,
            companyId: orderAskForQuote.buyer
              ? orderAskForQuote.buyer.company
              : undefined,
          }}
        />
      </Dialog>
      <Button
        color="primary"
        component={Link}
        to={path('procurement.incoming.enquiries', {
          companyID: company._id,
        })}
        startIcon={<ArrowBackIosIcon />}
      >
        {t('back')}
      </Button>
      <Paper square className={classes.paper}>
        <Grid container spacing={2}>
          <Grid container item xs={12} justifyContent="space-between">
            <Grid item>
              <Typography variant="h6">
                {t('enquiry')} #{orderAskForQuote._id}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip
                title={
                  isFreePackage
                    ? t(
                        'you_are_subscribed_to_free_package_please_upgrade_your_subscription_to_enjoy_more_benefit'
                      )
                    : ''
                }
              >
                <span>
                  <ButtonSubmit
                    disabled={isFreePackage}
                    color="primary"
                    variant="contained"
                    iconPosition="left"
                    onClick={onClickProposeQuotation}
                    isSubmitting={isProposingQuotationOrderAskForQuote}
                    label={t('propose__type__', { type: t('quotation') })}
                    icon={<PostAddIcon />}
                  />
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" className={classes.bold}>
                {t('from')} :
              </Typography>
            </Grid>
            <Grid container item xs={12} spacing={2} alignItems="center">
              {isFreePackage && (
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  style={{ position: 'relative' }}
                >
                  <FreePackagePlanSubscriptionPaper
                    style={{
                      position: 'absolute',
                      top: '50%',
                      zIndex: 100,
                    }}
                  />
                </Grid>
              )}
              {orderAskForQuote.buyer && orderAskForQuote.buyer.userData && (
                <Grid
                  container
                  item
                  xs={12}
                  spacing={2}
                  alignItems="center"
                  className={classNames({
                    [classes.blur]: isFreePackage,
                  })}
                >
                  <Grid item>
                    <SchemaUserAvatar
                      width={8}
                      height={8}
                      useIndexLink
                      user={orderAskForQuote.buyer.userData}
                    />
                  </Grid>
                  <Grid item>
                    <SchemaUserName
                      typography
                      typographyVariant="subtitle1"
                      useIndexLink
                      user={orderAskForQuote.buyer.userData}
                    />
                    <br />
                    <SchemaUserEmail
                      workProfileCompanyId={orderAskForQuote.buyer.company}
                      user={orderAskForQuote.buyer.userData}
                    />
                    {orderAskForQuote.buyer.companyData && (
                      <>
                        <br />
                        {buyerWorkProfile && buyerWorkProfile.position && (
                          <>{getText(buyerWorkProfile.position)}&nbsp;</>
                        )}
                        {buyerWorkProfile && buyerWorkProfile.startedAt && (
                          <>
                            <span
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: buyerWorkProfile.stoppedAt
                                  ? t('from__started__to__stopped__', {
                                      started: moment(
                                        buyerWorkProfile.startedAt
                                      ).format('MMMM YYYY'),
                                      stopped: buyerWorkProfile.stoppedAt
                                        ? moment(
                                            buyerWorkProfile.stoppedAt
                                          ).format('MMMM YYYY')
                                        : t('present'),
                                    })
                                  : t('from__started__', {
                                      started: moment(
                                        buyerWorkProfile.startedAt
                                      ).format('MMMM YYYY'),
                                    }),
                              }}
                            />
                            &nbsp;
                          </>
                        )}
                        <SchemaCompanyName
                          showVerification
                          typography
                          useIndexLink
                          company={orderAskForQuote.buyer.companyData}
                        />
                      </>
                    )}
                  </Grid>
                  <Grid container item xs={12} spacing={1}>
                    {buyerTel && (
                      <Grid item xs={4}>
                        <Button
                          fullWidth
                          color="primary"
                          variant="contained"
                          component="a"
                          href={tel.getLink(buyerTel)}
                          startIcon={<PhoneIcon />}
                        >
                          {t('call')}
                        </Button>
                      </Grid>
                    )}
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        onClick={() => setIsOpenMessageDialog(true)}
                        startIcon={<QuestionAnswerIcon />}
                      >
                        {t('message')}
                      </Button>
                    </Grid>
                    <Grid item xs={4}>
                      <ButtonFollow
                        fullWidth
                        userFollowId={
                          orderAskForQuote.buyer.userData.currentUserFollowData
                            ? orderAskForQuote.buyer.userData
                                .currentUserFollowData._id
                            : undefined
                        }
                        currentUserId={contextUser._id}
                        followUserId={orderAskForQuote.buyer.userData._id}
                        followType={UserFollow.TYPE.USER}
                        followText={t('follow')}
                        followedText={t('following')}
                        color="primary"
                        variant="contained"
                        startIcon={<StarBorderIcon />}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {department && department.name && (
              <>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    className={classes.bold}
                  >
                    {t('department')} :
                  </Typography>
                  <Typography variant="body2">
                    {getText(department.name)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                gutterBottom
                className={classes.bold}
              >
                {t('message')} :
              </Typography>
              <Typography variant="body2" className={classes.message}>
                {orderAskForQuote.message}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                display="inline"
                className={classes.bold}
              >
                {t('closing_date')} : &nbsp;
              </Typography>
              <Typography display="inline" className={classes.closedDateDanger}>
                {moment(orderAskForQuote.closed).format('D MMM YYYY')}&nbsp;
                {moment().isAfter(orderAskForQuote.closed) ? (
                  <>({t('expired')})</>
                ) : (
                  <>({moment(orderAskForQuote.closed).fromNow()})</>
                )}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {orderAskForQuote.items && orderAskForQuote.items.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    className={classes.bold}
                  >
                    {t('enquire_about')} :
                  </Typography>
                  <Grid container item xs={12} spacing={1}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">{t('name')}</TableCell>
                          <TableCell align="center">{t('variation')}</TableCell>
                          <TableCell align="center">{t('image')}</TableCell>
                          <TableCell align="center">{t('price')}</TableCell>
                          <TableCell align="right">{t('quantity')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderAskForQuote.items.map((item) => {
                          const { productData, id } = item;
                          if (!productData) return null;
                          const images =
                            productData.media && productData.media.images
                              ? productData.media.images
                              : [];
                          const image = images.find(
                            (tempImage) => tempImage.status
                          );

                          const productVariations = productData.variations
                            ? productData.variations
                            : [];
                          const productCustomFields = productData.custom_fields
                            ? productData.custom_fields
                            : [];

                          return (
                            <TableRow key={id}>
                              <TableCell align="center">
                                <Button
                                  color="primary"
                                  href={keepitalPath(
                                    'company.showcase.product.index',
                                    {
                                      origin:
                                        orderAskForQuote.seller.companyData &&
                                        orderAskForQuote.seller.companyData
                                          .origin
                                          ? orderAskForQuote.seller.companyData.origin.toLowerCase()
                                          : '',
                                      companySlug:
                                        orderAskForQuote.seller.companyData &&
                                        orderAskForQuote.seller.companyData.slug
                                          ? orderAskForQuote.seller.companyData
                                              .slug
                                          : '',
                                      productSlug: productData.slug,
                                    }
                                  )}
                                  target="_blank"
                                >
                                  {getText(productData.name)}
                                </Button>
                              </TableCell>
                              <TableCell align="center">
                                {item.variations && item.variations.length
                                  ? item.variations.map((itemVariation) => {
                                      const productVariation = productVariations.find(
                                        (tempVariation) =>
                                          tempVariation.id === itemVariation.id
                                      );

                                      if (productVariation) {
                                        return Object.keys(
                                          productVariation.attributes
                                        ).map((attributeKey) => {
                                          const companyCustomField = companyCustomFields.find(
                                            (tempCustomField) =>
                                              tempCustomField.id ===
                                              attributeKey
                                          );
                                          const productCustomField = productCustomFields.find(
                                            (tempCustomField) =>
                                              tempCustomField.id ===
                                              attributeKey
                                          );

                                          if (
                                            !companyCustomField ||
                                            !productCustomField ||
                                            !productCustomField.status
                                          )
                                            return null;

                                          if (attributeKey === 'prices') {
                                            return null;
                                          }
                                          return (
                                            <p key={attributeKey}>
                                              {getText(
                                                companyCustomField.title
                                              )}
                                              :{' '}
                                              {
                                                productVariation.attributes[
                                                  attributeKey
                                                ]
                                              }
                                            </p>
                                          );
                                        });
                                      }

                                      return null;
                                    })
                                  : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {image ? (
                                  <BaseImage
                                    src={
                                      image.filenameSmallUrl ||
                                      image.filenameUrl
                                    }
                                    style={{
                                      maxWidth: '100px',
                                      height: '35px',
                                    }}
                                    lightboxImages={images.map(
                                      (tempImage) => tempImage.filenameUrl
                                    )}
                                    title={getText(productData.name)}
                                    alt={getText(productData.name)}
                                  />
                                ) : (
                                  <span>{`${t('no')} ${t('image')}`}</span>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {item.variations && item.variations.length ? (
                                  item.variations.map((itemVariation) => {
                                    const productVariation = productVariations.find(
                                      (tempVariation) =>
                                        tempVariation.id === itemVariation.id
                                    );

                                    if (productVariation) {
                                      return Object.keys(
                                        productVariation.attributes
                                      ).map((attributeKey) => {
                                        const companyCustomField = companyCustomFields.find(
                                          (tempCustomField) =>
                                            tempCustomField.id === attributeKey
                                        );
                                        const productCustomField = productCustomFields.find(
                                          (tempCustomField) =>
                                            tempCustomField.id === attributeKey
                                        );

                                        if (
                                          !companyCustomField ||
                                          !productCustomField ||
                                          !productCustomField.status
                                        )
                                          return null;

                                        if (attributeKey !== 'prices') {
                                          return null;
                                        }
                                        return (
                                          <p key={attributeKey}>
                                            {productData &&
                                              productData.prices &&
                                              productData.prices.currency &&
                                              getSymbol(
                                                productData.prices.currency
                                              )}
                                            &nbsp;
                                            {
                                              productVariation.attributes[
                                                attributeKey
                                              ]
                                            }
                                          </p>
                                        );
                                      });
                                    }

                                    return null;
                                  })
                                ) : (
                                  <>
                                    {productData &&
                                    productData.prices &&
                                    productData.prices.currency &&
                                    productData.prices.final ? (
                                      <>
                                        {getSymbol(productData.prices.currency)}
                                        &nbsp;
                                        {productData.prices.final}
                                      </>
                                    ) : (
                                      '-'
                                    )}
                                  </>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                x
                                <strong className={classes.productQuantity}>
                                  {item.quantity}
                                  <br />
                                  {productData.infos &&
                                    productData.infos.unit && (
                                      <>{getText(productData.infos.unit)}</>
                                    )}
                                </strong>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>
      <Paper square className={classes.paperQuotation}>
        <ProcurementIncomingAskForQuoteDetailRelatedQuotationTable
          referenceId={orderAskForQuote._id}
        />
      </Paper>
    </>
  );
}

export default () => (
  <ProcurementLayout main={<ProcurementIncomingOrderAskForQuoteDetail />} />
);
